import { arrayHas } from "../arrayHas.js";
import { observable } from "../../r-html/dist/observable/index.js";
import { curveReach } from "./bezier.js";
import { loopReach } from "./loop.js";
//#region src/utils/draw-relationship/index.ts
var DirectionName = {
	left: "left",
	right: "right",
	top: "top",
	bottom: "bottom"
};
var DirectionNameList = Object.values(DirectionName);
arrayHas([
	DirectionName.top,
	DirectionName.bottom,
	DirectionName.left,
	DirectionName.right
]);
var EMPTY_SLOTS = [0, 0];
function copyAnchor({ tableId, x, y, direction }) {
	return {
		tableId,
		x,
		y,
		direction
	};
}
var createSortChannel = () => ({
	epoch: 0,
	stubSlots: /* @__PURE__ */ new WeakMap(),
	routes: /* @__PURE__ */ new WeakMap(),
	anchors: /* @__PURE__ */ new WeakMap()
});
var channels = {
	document: createSortChannel(),
	flow: createSortChannel()
};
var viewVersions = { flow: /* @__PURE__ */ new WeakMap() };
function viewVersionOf(relationship, source) {
	const versions = viewVersions[source];
	let held = versions.get(relationship);
	if (!held) {
		held = observable({ version: 0 });
		versions.set(relationship, held);
	}
	return held;
}
function observeView(relationship, source) {
	return viewVersionOf(relationship, source).version;
}
var routeVersions = /* @__PURE__ */ new WeakMap();
var heldRedraws = /* @__PURE__ */ new WeakMap();
function observeRoute(relationship) {
	let held = routeVersions.get(relationship);
	if (!held) {
		held = observable({ version: 0 });
		routeVersions.set(relationship, held);
	}
	return held.version;
}
function bumpRoute(relationship) {
	const held = routeVersions.get(relationship);
	if (held) held.version += 1;
}
function holdRouteRedraws(state) {
	if (!heldRedraws.has(state)) heldRedraws.set(state, /* @__PURE__ */ new Set());
}
function releaseRouteRedraws(state) {
	const held = heldRedraws.get(state);
	if (!held) return;
	heldRedraws.delete(state);
	for (const relationship of held) bumpRoute(relationship);
}
function heldRouteRedraws(state) {
	return heldRedraws.get(state);
}
function bumpView(relationship, source) {
	viewVersionOf(relationship, source).version += 1;
}
function clearSortChannel(relationships, source = "document") {
	const channel = channels[source];
	for (const relationship of relationships) {
		const hadSlots = channel.stubSlots.delete(relationship);
		const hadRoute = channel.routes.delete(relationship);
		const hadAnchors = channel.anchors.delete(relationship);
		if (source !== "document" && (hadSlots || hadRoute || hadAnchors)) bumpView(relationship, source);
	}
}
var sameSlots = (a, b) => a[0] === b[0] && a[1] === b[1];
var sameAnchor = (a, b) => a.x === b.x && a.y === b.y && a.direction === b.direction;
var ROUTE_EPSILON = .01;
var samePoints = (a, b) => a.length === b.length && a.every((point, index) => Math.abs(point.x - b[index].x) < ROUTE_EPSILON && Math.abs(point.y - b[index].y) < ROUTE_EPSILON);
function setStubSlots(relationship, slots, source = "document") {
	const channel = channels[source];
	if (source !== "document" && !sameSlots(channel.stubSlots.get(relationship) ?? EMPTY_SLOTS, slots)) bumpView(relationship, source);
	channel.stubSlots.set(relationship, slots);
}
function getStubSlots(relationship, source = "document") {
	if (source !== "document") observeView(relationship, source);
	return channels[source].stubSlots.get(relationship) ?? EMPTY_SLOTS;
}
function setRoute(relationship, points, source = "document", held) {
	const channel = channels[source];
	const was = channel.routes.get(relationship);
	if (!was || !samePoints(was.points, points)) {
		if (source !== "document") bumpView(relationship, source);
		else if (held) held.add(relationship);
		else bumpRoute(relationship);
	}
	channel.routes.set(relationship, {
		points,
		epoch: channel.epoch
	});
}
function getRoute(relationship, source = "document") {
	if (source === "document") observeRoute(relationship);
	else observeView(relationship, source);
	return channels[source].routes.get(relationship)?.points;
}
function nextSortEpoch(source = "document") {
	channels[source].epoch += 1;
}
function getAnchors(relationship, source = "document") {
	if (source === "document") return relationship;
	observeView(relationship, source);
	return channels[source].anchors.get(relationship) ?? relationship;
}
function setAnchors(relationship, { start, end }, source = "document") {
	if (source === "document") {
		relationship.start.direction = start.direction;
		relationship.start.x = start.x;
		relationship.start.y = start.y;
		relationship.end.direction = end.direction;
		relationship.end.x = end.x;
		relationship.end.y = end.y;
		return;
	}
	const { anchors } = channels[source];
	const seen = anchors.get(relationship) ?? relationship;
	if (!sameAnchor(seen.start, start) || !sameAnchor(seen.end, end)) bumpView(relationship, source);
	anchors.set(relationship, {
		start: copyAnchor(start),
		end: copyAnchor(end)
	});
}
function aabb(points) {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const { x, y } of points) {
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	return {
		x: minX,
		y: minY,
		width: maxX - minX,
		height: maxY - minY
	};
}
function inflate({ x, y, width, height }, padding) {
	return {
		x: x - padding,
		y: y - padding,
		width: width + padding * 2,
		height: height + padding * 2
	};
}
function relationshipStrokeWidth(source = "document") {
	return source === "document" ? 2 : 1;
}
function getRouteBBox(relationship, strokeWidth = 2, source = "document") {
	const channel = channels[source];
	const { start, end } = getAnchors(relationship, source);
	const entry = channel.routes.get(relationship);
	const routed = entry && entry.epoch === channel.epoch ? entry.points : null;
	if (start.tableId === end.tableId) return inflate(aabb([start, end]), 21 + strokeWidth + loopReach(start, end));
	const slack = routed ? 0 : 48;
	if (source !== "document") {
		const box = aabb([
			...routed?.length ? [routed[0], routed[routed.length - 1]] : [],
			start,
			end
		]);
		const reach = curveReach(Math.max(box.width, box.height) + 2 * slack);
		return inflate(box, 21 + strokeWidth + slack + reach);
	}
	return inflate(aabb(routed ? [
		...routed,
		start,
		end
	] : [start, end]), 21 + strokeWidth + slack);
}
//#endregion
export { DirectionName, DirectionNameList, clearSortChannel, copyAnchor, getAnchors, getRoute, getRouteBBox, getStubSlots, heldRouteRedraws, holdRouteRedraws, nextSortEpoch, relationshipStrokeWidth, releaseRouteRedraws, setAnchors, setRoute, setStubSlots };
